import { createTheme, Shadows } from '@mui/material/styles';
import colors from '../constants/colors';

/**
 * MUI Theme Configuration
 * Provides centralized theming for all Material-UI components
 * Integrates with existing color constants and design system
 */
export const theme = createTheme({
  // RTL support for Hebrew
  direction: 'rtl',

  // Color palette using existing color constants
  palette: {
    primary: {
      main: colors.PURPLE,
      light: colors.PURPLE_LIGHT,
      dark: colors.BLACK,
      contrastText: colors.WHITE,
    },
    secondary: {
      main: colors.GREEN,
      contrastText: colors.WHITE,
    },
    error: {
      main: colors.RED,
      dark: colors.RED_DARK,
      contrastText: colors.WHITE,
    },
    warning: {
      main: colors.ORANGE,
      contrastText: colors.WHITE,
    },
    success: {
      main: colors.GREEN,
      contrastText: colors.WHITE,
    },
    background: {
      default: colors.BACKGROUND,
      paper: colors.WHITE,
    },
    text: {
      primary: colors.BLACK,
      secondary: colors.GREY_7,
      disabled: colors.GREY_5,
    },
    divider: colors.GREY_3,
    action: {
      active: colors.PURPLE,
      hover: colors.GREY_1,
      selected: colors.PURPLE_LIGHTER,
      disabled: colors.GREY_4,
      disabledBackground: colors.GREY_2,
      focus: colors.PURPLE_LIGHT,
    },
    grey: {
      50: colors.GREY_1,
      100: colors.GREY_2,
      200: colors.GREY_3,
      300: colors.GREY_4,
      400: colors.GREY_5,
      500: colors.GREY_6,
      600: colors.GREY_7,
      700: colors.GREY_7,
      800: colors.BLACK,
      900: colors.BLACK,
    },
  },

  // Typography scale based on Rubik font
  typography: {
    fontFamily: '"Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',

    // Headings
    h1: {
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: 'clamp(1.5rem, 4vw, 2rem)',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: 'clamp(1.25rem, 3vw, 1.75rem)',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },

    // Body text
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },

    // Button text
    button: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: 'none', // Don't uppercase buttons
    },

    // Captions and subtitles
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: colors.GREY_6,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
    },
  },

  // Spacing system (8px base unit)
  spacing: 8,

  // Border radius
  shape: {
    borderRadius: 12,
  },

  // Shadows - custom shadow system (25 levels: index 0-24)
  shadows: [
    'none',
    '0px 2px 4px rgba(31, 38, 135, 0.06)',
    '0px 2px 8px rgba(31, 38, 135, 0.08)',
    '0px 4px 12px rgba(31, 38, 135, 0.10)',
    '0px 4px 16px rgba(31, 38, 135, 0.12)',
    '0px 6px 20px rgba(31, 38, 135, 0.14)',
    '0px 8px 24px rgba(31, 38, 135, 0.16)',
    '0px 8px 28px rgba(31, 38, 135, 0.18)',
    '0px 12px 32px rgba(31, 38, 135, 0.20)',
    '0px 16px 40px rgba(31, 38, 135, 0.22)',
    '0px 20px 48px rgba(31, 38, 135, 0.24)',
    // Purple shadows for primary actions
    '0px 4px 12px rgba(116, 105, 182, 0.25)',
    '0px 8px 20px rgba(116, 105, 182, 0.30)',
    '0px 12px 28px rgba(116, 105, 182, 0.35)',
    '0px 16px 36px rgba(116, 105, 182, 0.40)',
    '0px 20px 44px rgba(116, 105, 182, 0.45)',
    // Glass shadow
    '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
    // Strong elevation
    '0px 24px 56px rgba(31, 38, 135, 0.28)',
    '0px 28px 64px rgba(31, 38, 135, 0.32)',
    '0px 32px 72px rgba(31, 38, 135, 0.36)',
    '0px 36px 80px rgba(31, 38, 135, 0.40)',
    '0px 40px 88px rgba(31, 38, 135, 0.44)',
    '0px 44px 96px rgba(31, 38, 135, 0.48)',
    '0px 48px 104px rgba(31, 38, 135, 0.52)',
    '0px 52px 112px rgba(31, 38, 135, 0.56)', // 25th element (index 24)
  ] as Shadows,

  // Component-specific customizations
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1.125rem',
          padding: '0.875rem 1.5rem',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0px 0px 0px 4px ${colors.FOCUS_RING}`,
          },
        },
        contained: {
          boxShadow: '0px 4px 12px rgba(116, 105, 182, 0.25)',

          '&:hover': {
            boxShadow: '0px 8px 20px rgba(116, 105, 182, 0.30)',
            transform: 'translateY(-2px)',
          },

          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        outlined: {
          borderWidth: '2px',

          '&:hover': {
            borderWidth: '2px',
            backgroundColor: colors.GREY_1,
          },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,

            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.PURPLE_LIGHT,
            },

            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: '2px',
              borderColor: colors.PURPLE,
            },
          },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
        },
        input: {
          padding: '1rem',
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 500,

          '&.Mui-focused': {
            color: colors.PURPLE,
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 2px 8px rgba(31, 38, 135, 0.08)',
          border: `1px solid ${colors.GREY_2}`,
          transition: 'all 0.2s ease',

          '&:hover': {
            boxShadow: '0px 8px 24px rgba(31, 38, 135, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0px 2px 8px rgba(31, 38, 135, 0.08)',
        },
        elevation2: {
          boxShadow: '0px 4px 16px rgba(31, 38, 135, 0.12)',
        },
        elevation3: {
          boxShadow: '0px 8px 24px rgba(31, 38, 135, 0.16)',
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: colors.PURPLE,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          height: 8,
          backgroundColor: colors.GREY_2,
        },
        bar: {
          borderRadius: 8,
          background: colors.GRADIENT_PURPLE,
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          padding: '1rem',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.5rem',
          fontWeight: 700,
          padding: '1.5rem',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '1.5rem',
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '1rem 1.5rem 1.5rem',
          gap: '0.75rem',
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease',

          '&:hover': {
            backgroundColor: colors.GREY_1,
            transform: 'scale(1.05)',
          },

          '&:active': {
            transform: 'scale(0.95)',
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          padding: '0.75rem 1rem',

          '&:hover': {
            backgroundColor: colors.GREY_1,
          },

          '&.Mui-selected': {
            backgroundColor: colors.PURPLE_LIGHTER,

            '&:hover': {
              backgroundColor: colors.PURPLE_LIGHT,
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.BLACK,
          fontSize: '0.875rem',
          borderRadius: 8,
          padding: '0.5rem 0.75rem',
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: 8,
        },
        track: {
          borderRadius: 12,
          backgroundColor: colors.GREY_3,
        },
        thumb: {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colors.GREY_4,

          '&.Mui-checked': {
            color: colors.PURPLE,
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: colors.GREY_4,

          '&.Mui-checked': {
            color: colors.PURPLE,
          },
        },
      },
    },
  },

  // Breakpoints (for responsive design)
  breakpoints: {
    values: {
      xs: 0,
      sm: 450,   // Mobile
      md: 768,   // Tablet
      lg: 1024,  // Desktop
      xl: 1280,
    },
  },

  // Transitions
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },

  // Z-index scale
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
});

export default theme;
